import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import JsonLd from "@/components/base/JsonLd";
import LeakCard from "@/components/feature/LeakCard";
import { leakCategories, leakEntries } from "@/mocks/leaks";

const SITE = "https://esjxdt.cn";

const sorts = [
  { key: "latest", label: "最新发布" },
  { key: "views", label: "阅读最多" },
  { key: "replies", label: "讨论最多" },
];

export default function LeaksPage() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") ?? "all";
  const [keyword, setKeyword] = useState(params.get("q") ?? "");
  const [sort, setSort] = useState("latest");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = leakEntries.filter((entry) => {
      const matchCat = activeCat === "all" || entry.categorySlug === activeCat;
      const matchKw =
        kw === "" ||
        entry.title.toLowerCase().includes(kw) ||
        entry.summary.toLowerCase().includes(kw) ||
        entry.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
    const sorted = [...list];
    if (sort === "views") sorted.sort((a, b) => b.views - a.views);
    else if (sort === "replies") sorted.sort((a, b) => b.replies - a.replies);
    else sorted.sort((a, b) => (a.date < b.date ? 1 : -1));
    return sorted;
  }, [activeCat, keyword, sort]);

  const changeCat = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("cat");
    else next.set("cat", slug);
    setParams(next, { replace: true });
  };

  const activeName =
    leakCategories.find((cat) => cat.slug === activeCat)?.name ?? "全部线索";

  const listJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: `${activeName} - 深夜爆料线索库`,
        url: `${SITE}/leaks`,
        inLanguage: "zh-CN",
        description: "深夜爆料线索库汇总匿名线人、知情者与内部来源提交并完成初步整理的线索条目。",
        isPartOf: { "@type": "WebSite", name: "深夜爆料", url: `${SITE}/` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "线索库", item: `${SITE}/leaks` },
        ],
      },
      {
        "@type": "ItemList",
        name: "深夜爆料线索列表",
        numberOfItems: filtered.length,
        itemListElement: filtered.slice(0, 8).map((entry, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: entry.title,
          url: `${SITE}/leaks/${entry.id}`,
        })),
      },
    ],
  };

  return (
    <SiteLayout>
      <title>线索库 | 深夜爆料匿名线索整理与信源汇总</title>
      <meta
        name="description"
        content="深夜爆料线索库收录影视剧组、综艺选秀、演出舞台、音乐产业、幕后职场与品牌风云六个方向的匿名投稿，每条线索标注来源描述与可信等级。"
      />
      <meta name="keywords" content="深夜爆料线索库,深夜爆料来源,深夜爆料匿名投稿,娱乐行业线索" />
      <link rel="canonical" href={`${SITE}/leaks`} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="线索库 | 深夜爆料匿名线索整理与信源汇总" />
      <meta property="og:description" content="汇总六个方向的匿名投稿线索，每条标注来源描述与可信等级。" />
      <meta property="og:url" content={`${SITE}/leaks`} />
      <meta name="twitter:card" content="summary_large_image" />
      <JsonLd data={listJsonLd} />

      <div className="pt-16">
        <section className="ink-panel relative overflow-hidden">
          <div className="grid-rule absolute inset-0 opacity-30" />
          <div className="wrap relative py-12 md:py-16">
            <nav aria-label="面包屑" className="flex items-center gap-2 font-label text-xs text-background-400">
              <Link to="/" className="transition-colors hover:text-accent-300">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-200">线索库</span>
            </nav>
            <h1 className="mt-5 font-heading text-3xl font-black text-background-50 md:text-5xl">
              线索库
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-300 md:text-base">
              这里存放已经完成初步整理的投稿。每条线索都有独立编号、来源描述与当前可信等级，
              等级会随着新信源的出现被上调或下调。当前共收录 {leakEntries.length} 条。
            </p>
          </div>
        </section>

        <section className="border-b border-background-200 bg-background-50">
          <div className="wrap py-8 md:py-10">
            <div className="flex flex-col gap-5">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => changeCat("all")}
                  className={`rounded-full px-4 py-2 font-label text-sm transition-colors cursor-pointer ${
                    activeCat === "all"
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  全部
                  <span className="ml-1 text-xs opacity-70">{leakEntries.length}</span>
                </button>
                {leakCategories.map((cat) => (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => changeCat(cat.slug)}
                    className={`rounded-full px-4 py-2 font-label text-sm transition-colors cursor-pointer ${
                      activeCat === cat.slug
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {cat.name}
                    <span className="ml-1 text-xs opacity-70">{cat.count}</span>
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:max-w-xs">
                  <span className="absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                    <i className="ri-search-line text-base" />
                  </span>
                  <input
                    type="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索标题、摘要或标签"
                    aria-label="搜索线索"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 font-label text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <span className="font-label text-xs text-foreground-500">
                    共 {filtered.length} 条结果
                  </span>
                  <div className="flex items-center gap-1 rounded-full border border-background-300 p-1">
                    {sorts.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setSort(item.key)}
                        className={`rounded-full px-3 py-1.5 font-label text-xs transition-colors cursor-pointer ${
                          sort === item.key
                            ? "bg-foreground-950 text-background-50"
                            : "text-foreground-600 hover:text-foreground-950"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="wrap py-12 md:py-16">
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map((entry) => (
                  <LeakCard key={entry.id} entry={entry} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <p className="font-heading text-lg font-bold text-foreground-950">
                  没有找到匹配的线索
                </p>
                <p className="max-w-md text-sm leading-relaxed text-foreground-600">
                  换个关键词试试，或者直接把你掌握的情况投递给我们——新线索会从这里开始。
                </p>
                <Link
                  to="/#submit"
                  className="btn-base bg-primary-500 px-5 py-2.5 text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-mic-line text-base" />
                  匿名投稿
                </Link>
              </div>
            )}

            <div className="mt-14 flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-100 px-6 py-10 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-mail-send-line text-xl" />
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                你手里也有一条没被说出来的线索？
              </h2>
              <p className="max-w-xl text-sm leading-relaxed text-foreground-600">
                不需要署名，不需要解释动机。写清时间、环节与观察到的细节，夜班编辑会替你把它整理成可核对的样子。
              </p>
              <Link
                to="/#submit"
                className="btn-base bg-primary-500 px-6 py-3 text-background-50 hover:bg-primary-600"
              >
                <i className="ri-send-plane-2-line text-base" />
                前往匿名投稿入口
              </Link>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}